import {
  OrderType,
  TemporaryHours,
} from 'api/ordering-service/ordering-service.types';
import i18n from 'app-i18n';
import { DateTime, Interval } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import { blue, green } from '@mui/material/colors';
import { EventInput } from '@fullcalendar/core/index.js';
import {
  dayOfWeekMap,
  DUMMY_EVENT_ID,
  SetHoursFormDataProps,
} from '../location-hours.utils';
import { daysOfWeekOptionsLong } from 'components/forms/fields/days-multiple-select-field/days-multiple-select-field.utils';

export type ExtendedPropsType = {
  orderType: OrderType;
  eventType: 'TemporaryHours';
};

const createEvent = (
  date: DateTime,
  timeSlot: { startTime: string; endTime: string },
  orderType: string,
): EventInput => ({
  id: uuidv4(),
  title:
    orderType === 'PICKUP'
      ? i18n.t('LocationHours.eventTitles.pickup')
      : i18n.t('LocationHours.eventTitles.delivery'),
  start: DateTime.fromISO(`${date.toISODate()}T${timeSlot.startTime}`).toISO(),
  end: DateTime.fromISO(`${date.toISODate()}T${timeSlot.endTime}`).toISO(),
  extendedProps: {
    orderType,
    eventType: 'TemporaryHours',
  },
  color: orderType === 'PICKUP' ? blue[500] : green[500],
});

const generateEventsForDateRange = (
  startDate: string,
  endDate: string,
  timeSlots: { startTime: string; endTime: string }[],
  orderType: OrderType[],
): EventInput[] => {
  const events: EventInput[] = [];
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);
  const interval = Interval.fromDateTimes(
    start.startOf('day'),
    end.endOf('day'),
  );
  const days = interval.splitBy({ days: 1 }).map(d => d.start);

  days.forEach(date => {
    orderType.forEach(type => {
      timeSlots?.forEach(timeSlot => {
        events.push(createEvent(date, timeSlot, type));
      });
    });
  });

  return events;
};

export const generateBackgroundEvents = (
  normalEvents: EventInput[] = [],
  isShowingNormalHours = false,
): EventInput[] => {
  const bgEvents = new Map<string, EventInput>();
  const eventsGroupedByDay: { [key: number]: EventInput[] } = {};

  normalEvents.forEach(event => {
    bgEvents.set(event.id, {
      ...event,
      display: isShowingNormalHours ? 'background' : 'none',
    });
  });

  const bgEventArray = Array.from(bgEvents.values());

  // Process events to concatenate titles for same start time and day
  bgEventArray.forEach((event, index) => {
    bgEventArray.slice(index + 1).forEach(otherEvent => {
      if (
        event.daysOfWeek.toString() === otherEvent.daysOfWeek.toString() &&
        event.startTime === otherEvent.startTime &&
        event.endTime === otherEvent.endTime
      ) {
        event.title = i18n.t('LocationHours.eventTitles.pickup&Delivery');
        otherEvent.title = i18n.t('LocationHours.eventTitles.pickup&Delivery');
      }
    });
  });

  // Group events by daysOfWeek and sort by startTime
  bgEventArray.forEach(event => {
    event.daysOfWeek.forEach(day => {
      if (!eventsGroupedByDay[day]) {
        eventsGroupedByDay[day] = [];
      }
      eventsGroupedByDay[day].push(event);
    });
  });

  for (const day in eventsGroupedByDay) {
    eventsGroupedByDay[day].sort((a, b) =>
      a.startTime.localeCompare(b.startTime),
    );
  }

  // Flatten the grouped and sorted events into a single array
  const sortedBackgroundEvents = Object.values(eventsGroupedByDay).flat();

  // Compare current event with next event
  sortedBackgroundEvents.forEach((currentEvent, i) => {
    const nextEvent = sortedBackgroundEvents[i + 1];

    if (nextEvent) {
      const currentStartTime = DateTime.fromFormat(
        currentEvent.startTime,
        'HH:mm:ss',
      );
      const nextStartTime = DateTime.fromFormat(
        nextEvent.startTime,
        'HH:mm:ss',
      );
      const timeDifferenceInMinutes = nextStartTime.diff(
        currentStartTime,
        'minutes',
      ).minutes;

      currentEvent.extendedProps = {
        ...currentEvent.extendedProps,
        nextTimeSlotHasEvent:
          timeDifferenceInMinutes >= 30 && timeDifferenceInMinutes < 60,
      };
    } else {
      currentEvent.extendedProps = {
        ...currentEvent.extendedProps,
        nextTimeSlotHasEvent: false,
      };
    }
  });

  return sortedBackgroundEvents;
};

export const convertTemporaryHoursToEventInput = (
  hours: TemporaryHours[],
  orderType: OrderType[],
): EventInput[] => {
  if (!hours) return [];

  return hours.reduce((acc: EventInput[], curr) => {
    // Handle sameHoursPerDate
    if (curr.sameHoursPerDate) {
      const sameHoursEvents = generateEventsForDateRange(
        curr.startDate,
        curr.endDate,
        curr.sameHoursPerDate.timeSlots,
        orderType,
      );
      acc = acc.concat(sameHoursEvents);
    }

    // Handle diffHoursPerDate
    if (curr.diffHoursPerDate) {
      Object.entries(curr.diffHoursPerDate).forEach(([date, hours]) => {
        const diffHoursEvents = generateEventsForDateRange(
          date,
          date,
          hours.timeSlots,
          orderType,
        );
        acc = acc.concat(diffHoursEvents);
      });
    }

    return acc;
  }, []);
};

/**
 * Groups by order type and then by day. All events for a single day are grouped together. So, each column in the calendar represents
 * a single entry in pickup/deliveryTemporaryHours. There can be multiple time slots in each of these entries.
 *
 * This deviates from the original design of this api, which is horrible. I don't care how the admin portal organizes this stuff.
 * We're always putting all of the time slots for a single day in a single entry.
 */
export const eventsToTemporaryHours = (events: EventInput[]) => {
  const grouped = events
    .filter(event => !event.display && !event.allDay)
    .reduce((acc, { start, end, extendedProps: { orderType } }) => {
      const startDate = DateTime.fromISO(String(start)).toISODate();
      const timeSlot = {
        startTime: DateTime.fromISO(String(start)).toFormat('HH:mm:ss'),
        endTime: DateTime.fromISO(String(end)).toFormat('HH:mm:ss'),
      };

      const orderTypeGroup = acc[orderType] ?? [];
      let dayGroup = orderTypeGroup.find(
        entry => entry.startDate === startDate,
      );

      if (!dayGroup) {
        dayGroup = {
          startDate,
          endDate: startDate,
          sameHoursPerDate: {
            dayOfWeek: DateTime.fromISO(startDate)
              .toFormat('cccc')
              .toUpperCase(),
            timeSlots: [],
          },
          diffHoursPerDate: null,
        };
        orderTypeGroup.push(dayGroup);
      }

      dayGroup.sameHoursPerDate.timeSlots.push(timeSlot);
      acc[orderType] = orderTypeGroup;

      return acc;
    }, {});

  return {
    pickupTemporaryHours: grouped.PICKUP || [],
    deliveryTemporaryHours: grouped.DELIVERY || [],
  };
};

export const convertSetTemporaryHoursFormDataToEventSource = (
  data: SetHoursFormDataProps,
): EventInput[] => {
  const { dateRange, timeRange, orderType } = data;

  const startDate = DateTime.fromISO(dateRange[0]).toFormat('yyyy-MM-dd');
  const endDate = DateTime.fromISO(dateRange[1]).toFormat('yyyy-MM-dd');
  const timeRangeFormatted = [
    {
      startTime: DateTime.fromISO(timeRange[0]).toFormat('HH:mm:ss'),
      endTime: DateTime.fromISO(timeRange[1]).toFormat('HH:mm:ss'),
    },
  ];

  return generateEventsForDateRange(
    startDate,
    endDate,
    timeRangeFormatted,
    orderType,
  );
};

export const filterTemporaryHoursOverlappedEvents = (
  data: SetHoursFormDataProps,
  existingEvents: EventInput[],
): EventInput[] => {
  const { eventId, orderType, timeRange, dateRange } = data;

  if (
    !timeRange.every(time => DateTime.fromISO(time).isValid) ||
    !dateRange.every(date => DateTime.fromISO(date).isValid)
  ) {
    return [];
  }

  const newEvents = convertSetTemporaryHoursFormDataToEventSource(data);

  const convertToInterval = event => {
    return Interval.fromDateTimes(
      DateTime.fromISO(event.start),
      DateTime.fromISO(event.end),
    );
  };

  const isOverlap = (eventInterval, newEventInterval) => {
    return eventInterval.overlaps(newEventInterval);
  };

  const newEventsIntervals = newEvents?.map(convertToInterval);

  // Filter events from eventArray based on matching day of week and order type
  return existingEvents.filter(event => {
    const existingEventInterval = convertToInterval(event);

    return (
      ![eventId, DUMMY_EVENT_ID].includes(event.id) &&
      !['background', 'none'].includes(event.display) &&
      orderType.includes(event.extendedProps.orderType) &&
      event.start <= event.end &&
      newEventsIntervals.some(newEventInterval =>
        isOverlap(existingEventInterval, newEventInterval),
      )
    );
  });
};

const reverseDayOfWeekMap = Object.entries(dayOfWeekMap).reduce(
  (acc, [key, value]) => {
    acc[value] = key;
    return acc;
  },
  {},
);

const formatTime = (startTime, endTime) => {
  return `${DateTime.fromISO(startTime).toFormat(
    'hh:mm a',
  )} - ${DateTime.fromISO(endTime).toFormat('hh:mm a')}`;
};

const getTranslatedDayLabel = dayId => {
  return daysOfWeekOptionsLong().find(day => day.id === dayId)?.label;
};

export const getGroupedBackgroundEvents = events => {
  const grouped = {};

  events.forEach(event => {
    const day = reverseDayOfWeekMap[event.daysOfWeek[0]];
    const formattedTime = formatTime(event.startTime, event.endTime);

    if (!grouped[day]) {
      grouped[day] = { pickup: new Set(), delivery: new Set() };
    }

    if (
      event.title.includes(i18n.t('LocationHours.eventTitles.pickup&Delivery'))
    ) {
      grouped[day].pickup.add(formattedTime);
      grouped[day].delivery.add(formattedTime);
    } else if (
      event.title.includes(i18n.t('LocationHours.eventTitles.pickup'))
    ) {
      grouped[day].pickup.add(formattedTime);
    } else {
      grouped[day].delivery.add(formattedTime);
    }
  });

  // Convert Sets to arrays and filter out days where both arrays are empty
  return Object.keys(dayOfWeekMap)
    .map(day => {
      const pickup = Array.from(grouped[day]?.pickup || []);
      const delivery = Array.from(grouped[day]?.delivery || []);

      const translatedDayLabel = getTranslatedDayLabel(day);

      // Create day object with non-empty arrays only
      const dayObj = {
        day: translatedDayLabel,
        ...(pickup.length > 0 && { pickup }),
        ...(delivery.length > 0 && { delivery }),
      };

      return pickup.length > 0 || delivery.length > 0 ? dayObj : null;
    })
    .filter(dayObj => dayObj !== null); // Remove null entries
};
